import { QueryFunctionContext } from '@tanstack/react-query';
import { expand } from 'uri-template-lite';
import { apiClient } from '../../common/ApiClient';
import {
  AUTOCOMPLETE_PAGE_SIZE,
  OptionsPage,
  OptionType,
  Page,
} from '../../common/queryClient';
import type { DataObjectSearchResultType } from '../schema';
import { DataObjectUrl } from './DataObjectUrl';
import { DataObjectOutputType, DataObjectType } from '../types';
import { useInfiniteSearchQuery } from '../../common/queryClient/useInfiniteSearchQuery';
import { formatCodeName } from '../../common/utils';

export interface DataObjectSearchQueryParams {
  searchText: string;
  dataObjectType?: DataObjectType;
  outputTypeCodes?: DataObjectOutputType[];
  excludedOutputTypeCodes?: DataObjectOutputType[];
  page?: number;
  size?: number;
}

export interface DataObjectInfiniteSearchOptions {
  dataObjectType?: DataObjectType;
  outputTypeCodes?: DataObjectOutputType[];
  excludedOutputTypeCodes?: DataObjectOutputType[];
}
export interface DataObjectOption extends OptionType {
  item: DataObjectSearchResultType;
}

const searchDataObjects = ({
  searchText,
  dataObjectType,
  outputTypeCodes,
  excludedOutputTypeCodes,
  page,
  size,
}: DataObjectSearchQueryParams) =>
  apiClient
    .get<Page<DataObjectSearchResultType>>(
      expand(DataObjectUrl.AUTOCOMPLETE, {
        searchText,
        dataObjectType,
        outputTypeCodes,
        excludedOutputTypeCodes,
        page,
        size,
      }),
    )
    .then((response) => response.data);

const searchDataObjectOptions = async ({
  meta,
  pageParam,
}: QueryFunctionContext<any, number>): Promise<
  OptionsPage<DataObjectOption>
> => {
  const page = pageParam ?? 0;
  const result = await searchDataObjects({
    ...meta,
    searchText: (meta?.searchText ?? '') as string,
    page,
    size: AUTOCOMPLETE_PAGE_SIZE,
  });

  const content: DataObjectOption[] = result.content.map((item) => {
    return {
      value: item.id,
      label: formatCodeName(item),
      item,
    };
  });
  return {
    content,
    last: result.last,
    number: result.number,
  };
};

export function useInfiniteSearchDataObjectsQuery(
  term: string,
  options?: DataObjectInfiniteSearchOptions,
) {
  return useInfiniteSearchQuery(
    ['search-data-objects-autocomplete'],
    searchDataObjectOptions,
    term,
    options,
  );
}
